import { PropType, reactive } from 'vue';
import BaseSetupService from '@/setup/BaseSetupService';
import { useModal } from '@orion.ui/orion';
import TempWorkerDocumentEntity from '@/entity/TempWorkerDocumentEntity';
import { TEMP_WORKER_DOCUMENT_TYPE } from '@/typings/enums/TEMP_WORKER_DOCUMENT_TYPE';
import UserProfileProSkillAsideNew from '@/components/user-profile/UserProfileProSkillAsideNew.vue';
import UserProfileInformationsEntity from '@/entity/user-profile/UserProfileInformationsEntity';
import UserProfileProExpAsideNewSetupService from '@/components/user-profile/UserProfileProExpAsideNew.vue';

type Props = SetupProps<typeof DashboardTempWorkerProfileCompletionStickerSetupService.props>
type Emits = { (e: 'uploaded'): void }

export default class DashboardTempWorkerProfileCompletionStickerSetupService extends BaseSetupService<Props> {
	static readonly props = {
		title: {
			type: String,
			default: null,
		},
		subtitle: {
			type: String,
			default: null,
		},
		icon: {
			type: String,
			default: null,
		},
		alt: {
			type: String,
			default: null,
		},
		isMissing: {
			type: Boolean,
			default: null,
		},
		typeLabel: {
			type: String,
			default: null,
		},
		documentType: {
			type: Number as PropType<TEMP_WORKER_DOCUMENT_TYPE>,
			default: undefined,
		},
		user: {
			type: Object,
			default: null,
		},
	};

	private readonly state = reactive({ typeLabel: '' });
	private emits: Emits;

	tempworkerDocument = this.props.documentType !== undefined ? new TempWorkerDocumentEntity({
		tempWorkerDocumentType: this.props.documentType,
		id: 0,
		userId: this.self.id,
		documentCreatedDate: new Date().toPost(),
	}) : undefined;

	readonly docs = ['CurriculumVitae', 'Diploma', 'IdentityPiece', 'SocialCertificate'];

	get iconLabel () {
		return this.props.typeLabel === 'IdentityPiece' ? 'IdentityCard' : this.props.typeLabel;
	}

	get typeOfDoc () {
		return this.docs.includes(this.props.typeLabel) ? 'docs' : 'informations';
	}

	get buttonColor () {
		return this.props.isMissing ? 'info' : 'default';
	}

	get buttonIcon () {
		if (this.typeOfDoc === 'docs') {
			if (this.props.isMissing) {
				return 'icon-programming-cloud-upload';
			} else {
				return 'icon-interface-arrows-button-right';
			}
		} else {
			if (this.props.isMissing) {
				return 'icon-interface-add-circle';
			} else {
				return 'icon-interface-arrows-button-right';
			}
		}
	}

	get subTitle () {
		const femininDocTypeName = ['Expériences', 'Carte d\'identité', 'Qualifications', 'Att. de sécurité sociale'];
		const splitTitle = this.props.title.split('');
		if (!this.props.isMissing) {
			if (splitTitle[splitTitle.length - 1] === 's') {
				return femininDocTypeName.includes(this.props.title) ? `${this.props.title} enregistrées` : `${this.props.title} enregistrés`;
			} else if (this.props.title === 'Att. de sécurité sociale') {
				return 'Attestation enregistrée';
			} else {
				return femininDocTypeName.includes(this.props.title) ? `${this.props.title} enregistrée` : `${this.props.title} enregistré`;
			}
		} else {
			if (splitTitle[splitTitle.length - 1] === 's') {
				return femininDocTypeName.includes(this.props.title) ? `Renseignez vos ${this.props.title}` : `Renseignez vos ${this.props.title}`;
			} else if (this.props.title === 'CV/Curriculum Vitae') {
				return `Renseignez votre CV`;
			} else if (this.props.title === 'Att. de sécurité sociale') {
				return `Renseignez votre attestation`;
			} else {
				return `Renseignez votre ${this.props.title}`;
			}
		}
	}

	constructor (props: Props, emits: Emits) {
		super(props);
		this.emits = emits;
	}

	buttonAction () {
		if (!this.props.isMissing) {
			this.router.push({
				name: this.typeOfDoc === 'docs' ? `UserProfileTempWorkerDocuments` : 'UserProfileInformations',
				hash: `#${this.props.typeLabel}`,
			});
		} else {
			if (this.typeOfDoc === 'docs') {
				this.tempworkerDocument?.openTempWorkerDocumentUploadModalAsync();
			} else if (this.props.typeLabel === 'Skill' || this.props.typeLabel === 'Experience') {
				this[`openAsideAddProfessional${this.props.typeLabel}`]();
			}
		}
	}

	openAsideAddProfessionalSkill () {
		useModal({
			Nested: UserProfileProSkillAsideNew,
			NestedProps: { userProfileInformationsEntity: new UserProfileInformationsEntity() },
			size: 'sm',
			events: {
				'added': () => {
					this.emits('uploaded');
				},
			},
		});
	}

	openAsideAddProfessionalExperience (exp = null) {
		useModal({
			Nested: UserProfileProExpAsideNewSetupService,
			NestedProps: { exp },
			size: 'sm',
			events:
			{
				'added': () => {
					this.emits('uploaded');
				},
			},
		});
	}
}
